import api, { centreApi, V2 } from './axios';

export interface GooglePlace {
  place_id: string;
  name: string;
  address: string;
  rating?: number | null;
  review_count?: number;
  url?: string;
  /** Google's pin for the listing. Stored beside the clinic's own location, never over it. */
  location?: { lat: number; lng: number } | null;
  linked_at?: string;
}

export interface CentreService {
  name: string;
  /** Canonical catalog slug - the stable key linking this service to the
   *  Path A/B browse tree. Present on migrated + newly onboarded centres. */
  slug?: string;
  category?: string;
  type?: string;
  price?: number;
  description?: string;
  Description?: string;
  duration?: number;
  // Onboarding: per-service home-visit availability (Step 2 toggles).
  home_visit?: boolean;
  // Optional package pricing entered in onboarding Step 3.
  package_sessions?: number;
  package_price?: number;
  /** Package discount %, synced with package_price (PDF §13). */
  package_discount?: number;
  /** `verified` + `date` are set on reviews written by customers after a booking. */
  reviews?: CentreReviewEntry[];
}

export interface CentreTherapist {
  name: string;
  qualification?: string;
  specialisation?: string;
  experience?: number;
  /** Hospital or practice the practitioner is attached to, if any. */
  affiliation?: string;
}

export interface CentreSlot {
  open: string;   // "HH:MM"
  close: string;  // "HH:MM"
}

export interface CentreTimingDay {
  day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
  closed: boolean;
  /** Multi-slot availability (morning + evening etc.). */
  slots?: CentreSlot[];
  /** Legacy single window - still accepted by the backend for back-compat. */
  open?: string;
  close?: string;
}

/** A public review. `verified` + `date` come from reviews written on Recovry after a booking. */
export interface CentreReviewEntry {
  rating: number;
  comment?: string;
  user?: string;
  date?: string;
  verified?: boolean;
  source?: 'recovry' | 'google';
}

export interface Centre {
  id: string;
  /** Clinic-level reviews: imported or not tied to one service. Service reviews live on services[]. */
  reviews?: CentreReviewEntry[];
  name: string;
  address: string;
  phone?: string;
  email?: string;
  whatsapp?: string;
  city?: string;
  state?: string;
  pincode?: string;
  location?: { lat: number; lng: number };
  images?: string[];
  image?: string;
  image2?: string;
  image3?: string;
  image4?: string;
  services: CentreService[];
  therapists?: CentreTherapist[];
  languages?: string[];
  timings?: CentreTimingDay[];
  blocked_dates?: string[];
  home_visit_charge?: number;
  home_visit_coverage?: 'full_city' | 'selected_areas' | 'none';
  home_visit_areas?: string;
  notes?: string;
  /** Hospitals or practices the clinic works with. Shown to patients when set. */
  hospital_affiliation?: string;
  /** Kept out of the public payload: it is for our own verification. */
  google_listing_url?: string;
  /** The Google Business listing the partner linked. A separate record: the
   *  clinic's own name and address are never overwritten from it. */
  google_place?: GooglePlace | null;
  free_listing?: { code: string; months: number; granted_at?: string; expires_at?: string };
  status?: string;
  rejection_reason?: string | null;
  distance_km?: number;
}

export interface ListingPromo {
  code: string;
  months: number;
  description: string;
}

export interface CentreBooking {
  booking_id: string;
  user_email?: string;
  service_name?: string;
  service_category?: string;
  service_price?: number;
  scheduled_date?: string;
  scheduled_time?: string;
  status?: string;
  centre_id?: string;
  booking_for?: string | { name?: string; phone?: string; self?: boolean };
  created_at?: string;
  /** 'recovry' = booked through the platform, 'direct' = walk-in created by the clinic. */
  source?: 'recovry' | 'direct';
  /** Present on Direct (walk-in) bookings created from the clinic portal. */
  patient_name?: string;
  patient_phone?: string;
  /** Where the session happens — 'home' = home visit, 'clinic' = at the clinic. */
  visit_type?: 'home' | 'clinic';
  home_visit_charge?: number;
  /** The patient's address to visit, present on home-visit bookings. */
  home_address?: {
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    landmark?: string;
    phone?: string;
    lat?: number;
    lng?: number;
  } | null;
  /** Coupon the customer redeemed on this booking (copied from the payment). */
  coupon_code?: string | null;
  coupon_discount?: number;
  [key: string]: unknown;
}

export interface CreateDirectBookingInput {
  patient_name: string;
  patient_phone: string;
  /** One of service_name / service_index identifies the centre's service. */
  service_name?: string;
  service_index?: number;
  /** Optional price override; defaults to the service's catalog price. */
  service_price?: number;
  scheduled_date: string;
  scheduled_time: string;
  status?: 'Confirmed' | 'Completed';
  notes?: string;
}

export interface CentreReview {
  rating: number;
  review?: string;
  user_email?: string;
  service_name?: string;
  created_at?: string;
}

export interface CentreEarnings {
  total: number;
  this_month: number;
  by_service: Array<{ service: string; amount: number }>;
}

export interface CentrePartnership {
  partner_since: string | null;
  valid_until: string | null;
  /** True once the partnership term has lapsed — the clinic is hidden from
   *  customers (website + app) and must renew to go live again. */
  expired?: boolean;
  referral_code: string;
  reward_months: number;
  referral_count: number;
  referral_message: string;
}

export interface PatientSummary {
  key: string;
  name: string;
  contact: string;
  primary_source: 'recovry' | 'direct';
  visit_count: number;
  last_visit?: string;
  services: string[];
}

export interface PatientHistory {
  patient: { key: string; name: string };
  total_visits: number;
  services_availed: Array<{ service_name: string; times: number }>;
  bookings: CentreBooking[];
}

export interface CentresListResponse {
  centres: Centre[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  sorted_by_location?: boolean;
}

export interface CentresListParams {
  categories?: string[];
  /** One catalog service slug (matches services[].slug). */
  service?: string;
  /** Any of these catalog service slugs (matches `?services=` on the API). */
  services?: string[];
  /** Free-text search across centre name + address (server-side). */
  q?: string;
  page?: number;
  limit?: number;
  lat?: number;
  lng?: number;
  /** Only centres that offer home visits (`?home_visit=true` on the API). */
  home_visit?: boolean;
}

export const centresApi = {
  list: (params: CentresListParams = {}, opts: { signal?: AbortSignal } = {}) => {
    const query: Record<string, string | number> = {};
    if (params.categories && params.categories.length) {
      query.categories = params.categories.join(',');
    }
    if (params.service) query.service = params.service;
    if (params.services && params.services.length) query.services = params.services.join(',');
    if (params.q) query.q = params.q;
    if (params.page) query.page = params.page;
    if (params.limit) query.limit = params.limit;
    if (typeof params.lat === 'number') query.lat = params.lat;
    if (typeof params.lng === 'number') query.lng = params.lng;
    if (params.home_visit) query.home_visit = 'true';
    return api.get<CentresListResponse>(`${V2}centres/`, { params: query, signal: opts.signal });
  },

  categories: () =>
    api.get<{ categories: string[] }>(`${V2}centres/categories/`),

  detail: (centreId: string) =>
    api.get<{ centre: Centre }>(`${V2}centres/${encodeURIComponent(centreId)}/`),

  // Cheapest price per service slug across active centres (for "Starting from Rs X").
  serviceMinPrices: () =>
    api.get<{ prices: Record<string, number> }>(`${V2}centres/service-prices/`),

  signup: (data: {
    name: string;
    email: string;
    password: string;
    phone: string;
    address: string;
    location?: { lat: number; lng: number };
    services: CentreService[];
    images?: string[];
    // Optional clinic onboarding metadata (forward-compatible with backend).
    whatsapp?: string;
    city?: string;
    state?: string;
    pincode?: string;
    therapists?: CentreTherapist[];
    languages?: string[];
    timings?: CentreTimingDay[];
    blocked_dates?: string[];
    home_visit_charge?: number;
    home_visit_coverage?: 'full_city' | 'selected_areas' | 'none';
    home_visit_areas?: string;
    notes?: string;
    /** Unified onboarding code — may be a free-listing promo OR a referral code;
     *  the backend decides which. (promo_code/referral_code kept for back-compat.) */
    code?: string;
    /** @deprecated use `code` */
    promo_code?: string;
    /** @deprecated use `code` */
    referral_code?: string;
    /** Marketing attribution session id (from the rc_attr_sid cookie), if any. */
    session_id?: string;
    /** Home-visit-only practitioner: no physical clinic (PDF Additional §home visit). */
    home_visit_only?: boolean;
    /** T&C + policies accepted (PDF Additional §2). Required by the backend. */
    accepted_policies?: boolean;
  }) =>
    api.post<{
      centre_id: string;
      status: string;
      free_listing_months?: number;
      // Auto-login: the backend now returns a centre session on signup so the
      // practitioner lands straight in the portal (no second login).
      access?: string;
      refresh?: string;
      centre?: { id: string; name: string; status: string };
    }>(`${V2}centres/signup/`, data),

  // Upload clinic photos to Cloudinary during onboarding. Returns the secure
  // URLs, which are then submitted as plain strings in `signup({ images })`.
  uploadImages: (files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('images', f));
    return api.post<{ urls: string[] }>(`${V2}centres/upload-image/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Free-listing promos - public: shown to centres during onboarding.
  listPromos: () =>
    api.get<{ promos: ListingPromo[] }>(`${V2}centres/promos/`),
  validatePromo: (code: string) =>
    api.post<{ valid: boolean; months?: number; description?: string; error?: string }>(
      `${V2}centres/promo/validate/`,
      { code },
    ),
  // Unified onboarding code check: the backend decides if it's a promo or a
  // referral code and returns which type it is + a friendly message.
  checkCode: (code: string) =>
    api.post<{
      valid: boolean;
      type: 'promo' | 'referral' | 'invalid';
      message?: string;
      error?: string;
      months?: number;
      reward_months?: number;
    }>(`${V2}centres/code/check/`, { code }),

  // Onboarding email verification (only when a centre opts to set a login email).
  sendEmailCode: (email: string) =>
    api.post<{ message: string }>(`${V2}centres/email/send-code/`, { email }),
  verifyEmailCode: (email: string, code: string) =>
    api.post<{ verified: boolean; error?: string }>(
      `${V2}centres/email/verify-code/`,
      { email, code },
    ),
  // Onboarding: verify a Google account as the clinic's login (instead of OTP).
  googleVerify: (token: string) =>
    api.post<{ email: string; verified: boolean }>(`${V2}centres/google-verify/`, { token }),
};

/* ------------------------------------------------------------------ */
/*  Centre-owner self-service portal API (separate auth)              */
/* ------------------------------------------------------------------ */
export interface CentreListUpdate {
  name?: string;
  phone?: string;
  whatsapp?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  location?: { lat: number; lng: number } | null;
  services?: CentreService[];
  images?: string[];
  timings?: CentreTimingDay[];
  blocked_dates?: string[];
  therapists?: CentreTherapist[];
  languages?: string[];
  home_visit_charge?: number;
  home_visit_coverage?: 'full_city' | 'selected_areas' | 'none';
  home_visit_areas?: string;
  notes?: string;
  hospital_affiliation?: string;
  google_listing_url?: string;
  /** null unlinks. */
  google_place?: GooglePlace | null;
}

interface CentreAuthResponse {
  access: string;
  refresh: string;
  centre: { id: string; name: string; status: string };
}

export const centrePortalApi = {
  // Login uses the public client - no token yet.
  login: (email: string, password: string) =>
    api.post<CentreAuthResponse>(`${V2}centres/login/`, { email, password }),

  // Password reset (email OTP). forgotPassword always 200s (no account enumeration).
  forgotPassword: (email: string) =>
    api.post<{ message: string }>(`${V2}centres/forgot-password/`, { email }),
  resetPassword: (email: string, code: string, newPassword: string, confirmPassword: string) =>
    api.post<CentreAuthResponse & { message: string }>(`${V2}centres/reset-password/`, {
      email,
      code,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),

  // Google sign-in for clinics: log in by matching the Google email to a listing.
  googleLogin: (token: string) =>
    api.post<CentreAuthResponse>(`${V2}centres/google-login/`, { token }),

  getMyListing: () => centreApi.get<{ centre: Centre }>(`${V2}centres/me/`),
  updateMyListing: (data: CentreListUpdate) =>
    centreApi.patch<{ centre: Centre }>(`${V2}centres/me/`, data),

  getMyBookings: (
    params: { status?: string; date?: string; source?: 'recovry' | 'direct'; page?: number; limit?: number } = {},
  ) =>
    centreApi.get<{
      bookings: CentreBooking[];
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    }>(`${V2}centres/me/bookings/`, { params }),
  updateBookingStatus: (bookingId: string, status: string) =>
    centreApi.patch<{ status: string }>(
      `${V2}centres/me/bookings/${encodeURIComponent(bookingId)}/`,
      { status },
    ),
  // Create a Direct (walk-in) booking from the clinic portal (source="direct").
  createDirectBooking: (data: CreateDirectBookingInput) =>
    centreApi.post<{ message: string; booking: CentreBooking }>(
      `${V2}centres/me/bookings/`,
      data,
    ),

  getMyReviews: () =>
    centreApi.get<{ reviews: CentreReview[]; average_rating: number; count: number }>(
      `${V2}centres/me/reviews/`,
    ),
  getMyEarnings: () => centreApi.get<CentreEarnings>(`${V2}centres/me/earnings/`),
  // Partnership status (Partner Since / Valid Until) + referral program info.
  getPartnership: () => centreApi.get<CentrePartnership>(`${V2}centres/me/partnership/`),

  // Patient records (PDF §4): distinct patients + per-patient treatment history.
  getPatients: (search?: string) =>
    centreApi.get<{ patients: PatientSummary[]; count: number }>(`${V2}centres/me/patients/`, {
      params: search ? { search } : {},
    }),
  getPatientHistory: (key: string) =>
    centreApi.get<PatientHistory>(`${V2}centres/me/patient-history/`, { params: { key } }),
};
